/**
 * SystemHealth — gateway probe + server-side service health via /api/v1/system/health.
 * Route: /admin/health
 */
import { useCallback, useEffect, useState } from "react";
import { AppBadge, AppButton, AppCard } from "../../../shared/components/ui";
import { IconSpinner } from "../../../shared/components/icons";
import { PageHeader } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";

type ServiceResult = {
  service: string;
  status: "healthy" | "unhealthy" | "unreachable";
  http_status: number | null;
  error?: string;
};

type CheckResult = {
  key: string;
  label: string;
  detail: string;
  status: "pending" | "ok" | "fail";
  latencyMs: number | null;
  message: string;
};

type SystemHealthResponse = {
  overall: "healthy" | "degraded";
  services: ServiceResult[];
};

async function timed<T>(fn: () => Promise<T>): Promise<{ ok: boolean; ms: number; message: string }> {
  const start = performance.now();
  try {
    await fn();
    return { ok: true, ms: Math.round(performance.now() - start), message: "Responded OK" };
  } catch (ex: unknown) {
    const ms = Math.round(performance.now() - start);
    const message = ex instanceof Error ? ex.message : "Request failed";
    return { ok: false, ms, message };
  }
}

const SERVICE_LABELS: Record<string, string> = {
  auth: "Auth Service",
  device: "Device Service",
  customer: "Customer Service",
  payment: "Payment Service",
  notification: "Notification Service",
  product: "Product Service",
  location: "Location Service",
  installation: "Installation Service",
  billing: "Billing Service",
  evaluator: "Evaluator Service",
};

export function SystemHealth() {
  const [gatewayCheck, setGatewayCheck] = useState<CheckResult>({
    key: "gateway", label: "API Gateway", detail: `${window.location.origin}/health`,
    status: "pending", latencyMs: null, message: "",
  });
  const [serviceChecks, setServiceChecks] = useState<CheckResult[]>([]);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<Date | null>(null);

  const runChecks = useCallback(async () => {
    setRunning(true);
    setGatewayCheck((c) => ({ ...c, status: "pending", latencyMs: null, message: "" }));
    setServiceChecks([]);

    const gateway = await timed(async () => {
      const res = await fetch(`${window.location.origin}/health`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    });
    setGatewayCheck((c) => ({
      ...c, status: gateway.ok ? "ok" : "fail", latencyMs: gateway.ms, message: gateway.message,
    }));

    try {
      const svcData = await api.get<SystemHealthResponse>("/system/health");
      setServiceChecks((svcData.services ?? []).map((s) => ({
        key: s.service,
        label: SERVICE_LABELS[s.service] ?? s.service,
        detail: `HTTP ${s.http_status ?? "—"}`,
        status: s.status === "healthy" ? "ok" : "fail",
        latencyMs: null,
        message: s.error ?? (s.status === "healthy" ? "Healthy" : s.status),
      })));
    } catch (ex: unknown) {
      setServiceChecks([{
        key: "system_health_api",
        label: "Services (server probe)",
        detail: "GET /api/v1/system/health",
        status: "fail",
        latencyMs: null,
        message: ex instanceof Error ? ex.message : "Request failed",
      }]);
    }

    setLastRun(new Date());
    setRunning(false);
  }, []);

  useEffect(() => { runChecks(); }, [runChecks]);

  const allChecks = [gatewayCheck, ...serviceChecks];
  const allOk = allChecks.length > 0 && allChecks.every((c) => c.status === "ok");
  const anyFail = allChecks.some((c) => c.status === "fail");

  function renderRow(c: CheckResult) {
    return (
      <tr key={c.key}>
        <td>{c.label}</td>
        <td className="pg-text-muted">{c.detail}</td>
        <td>
          {c.status === "pending" && <AppBadge tone="neutral"><IconSpinner size={12} /> Checking…</AppBadge>}
          {c.status === "ok" && <AppBadge tone="success">Healthy</AppBadge>}
          {c.status === "fail" && <AppBadge tone="error">Unreachable</AppBadge>}
        </td>
        <td style={{ textAlign: "right" }}>{c.latencyMs != null ? `${c.latencyMs} ms` : "—"}</td>
        <td className="pg-text-muted">{c.message || "—"}</td>
      </tr>
    );
  }

  return (
    <div>
      <PageHeader
        title="System health"
        subtitle="Gateway probe + server-side health check across all 10 services."
        actions={
          <AppButton type="button" onClick={runChecks} disabled={running}>
            {running ? <><IconSpinner size={14} /> Running…</> : "Run checks"}
          </AppButton>
        }
      />

      <div className="pg-stats-row">
        <AppCard>
          <div className="pg-stat-label">Overall Status</div>
          <div className="pg-stat-value">
            {running ? "Checking…" : allOk ? "Healthy" : anyFail ? "Degraded" : "Checking…"}
          </div>
          <div className="pg-stat-indicator" data-tone={running ? "info" : allOk ? "success" : anyFail ? "error" : "neutral"} />
        </AppCard>
        {lastRun && (
          <AppCard>
            <div className="pg-stat-label">Last Checked</div>
            <div className="pg-stat-value" style={{ fontSize: 18 }}>{lastRun.toLocaleTimeString()}</div>
          </AppCard>
        )}
      </div>

      <AppCard title="Checks">
        <table className="pg-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>HTTP / Endpoint</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Latency</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {renderRow(gatewayCheck)}
            {serviceChecks.map(renderRow)}
          </tbody>
        </table>
      </AppCard>
    </div>
  );
}
